import { Component, Event, EventEmitter, JSX, Prop, Watch, h } from '@stencil/core'

import { SelectOption, SelectOptionValue } from '../air-select-option/air-select-option.types'
import { Validation } from '../../types/status.types'
import { WorkersCompensationClass } from './air-workers-compensation-class.types'
import { WorkersCompensationClassValue } from '../air-question/air-question.types'

@Component({
  tag: 'air-workers-compensation-class',
  styleUrl: 'air-workers-compensation-class.scss',
  shadow: true,
})
export class AirWorkersCompensationClass {
  /** Value of workers compensation class */
  @Prop({ mutable: true }) value?: string

  /** Workers compensation classes */
  @Prop() workersCompensationClasses?: WorkersCompensationClass[]
  @Watch('workersCompensationClasses')
  watchWorkersCompensationClassesChange(): void {
    this.initOptions()
    this.value = undefined
  }

  /** Specifies validation of workers compensation class */
  @Prop() validation?: Validation

  /** Specifies if workers compensation class is required */
  @Prop() required = false

  /** Specifies label */
  @Prop() label?: string

  /** Specifies placeholder value */
  @Prop() placeholder?: string

  /** Emits when workers compensation class value was changed */
  @Event({ eventName: 'air-change' }) change?: EventEmitter<WorkersCompensationClassValue | null>

  private initialOptions?: SelectOption[]
  private flattenedWcClasses?: WorkersCompensationClass[]

  componentWillLoad(): void {
    this.initOptions()
  }

  private initOptions = (): void => {
    if (!this.workersCompensationClasses || this.workersCompensationClasses.length === 0) return

    this.flattenedWcClasses =
      this.workersCompensationClasses
        ?.flatMap((wcClass) => wcClass.children)
        .filter((wcClass): wcClass is WorkersCompensationClass => wcClass !== undefined) || []

    this.initialOptions =
      this.flattenedWcClasses
        .map(({ title, code }) => ({
          label: title,
          value: code,
          selected: this.value === code,
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }),
        ) || []
  }

  private handleChange = (event: CustomEvent<SelectOptionValue>): void => {
    event.stopPropagation()

    const wcClassValue = this.flattenedWcClasses?.find((wcClass) => wcClass.code === event.detail)
    const emittedWcClassValue = wcClassValue
      ? {
          code: wcClassValue.code,
          industry: wcClassValue.industry || '',
        }
      : null
    this.change?.emit(emittedWcClassValue)
  }

  render(): JSX.Element {
    const noResultsMessage = 'No results. Please try a different keyword or select another state.'
    const attributes = {
      class: 'form-element',
      required: this.required,
      label: this.label,
      placeholder: this.placeholder,
      'onAir-change': this.handleChange,
      validation: this.validation,
      options: this.initialOptions,
      filter: true,
      'no-results-message': noResultsMessage,
    }

    return (
      <div class="workers-compensation-class">
        <air-select {...attributes}></air-select>
      </div>
    )
  }
}
